import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { fetchLowStockReport } from '../../../api';

const STOCK_FILTERS = [
  { key: 'low_stock', label: 'Low Stock' },
  { key: 'critically_low_stock', label: 'Critically Low Stock' },
  { key: 'out_of_stock', label: 'Out of Stock' },
];

const getFilterLabel = (key) => {
  return STOCK_FILTERS.find((item) => item.key === key)?.label || 'Low Stock';
};

const formatMoney = (value) => {
  return `Rs. ${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-LK');

const formatExcelDateOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-LK');
};

const setColumnWidths = (worksheet, widths) => {
  worksheet['!cols'] = widths.map((width) => ({ wch: width }));
};

const buildLowStockRows = (reportData) => {
  return (reportData?.rows || []).map((item) => ({
    Item: item.itemName || '',
    Batch: item.batchNo || '',
    Strength: item.strength || '',
    Supplier: item.supplier || '',
    'Current Stock': item.currentStock || 0,
    'Low Stock Level': item.lowStockThreshold ?? '',
    'Critical Stock Level': item.criticalThreshold ?? '',
    'Stock Status': item.stockStatus || '',
    'Expiry Date': formatExcelDateOnly(item.expiryDate),
    'Expiry Status': item.expiryStatus || '',
    'Wholesale Price': item.wholesalePrice || 0,
    'Stock Value': item.stockValue || 0,
  }));
};

const downloadLowStockExcel = ({ filename, reportData }) => {
  if (!reportData) return;

  const rows = buildLowStockRows(reportData);

  const headers = [
    'Item',
    'Batch',
    'Strength',
    'Supplier',
    'Current Stock',
    'Low Stock Level',
    'Critical Stock Level',
    'Stock Status',
    'Expiry Date',
    'Expiry Status',
    'Wholesale Price',
    'Stock Value',
  ];

  const sheetRows = [
    headers,
    ...rows.map((row) => [
      row.Item,
      row.Batch,
      row.Strength,
      row.Supplier,
      row['Current Stock'],
      row['Low Stock Level'],
      row['Critical Stock Level'],
      row['Stock Status'],
      row['Expiry Date'],
      row['Expiry Status'],
      row['Wholesale Price'],
      row['Stock Value'],
    ]),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

  setColumnWidths(worksheet, [
    34, // Item
    16, // Batch
    14, // Strength
    24, // Supplier
    16, // Current Stock
    18, // Low Stock Level
    20, // Critical Stock Level
    22, // Stock Status
    16, // Expiry Date
    18, // Expiry Status
    18, // Wholesale Price
    16, // Stock Value
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Low Stock Report');

  XLSX.writeFile(workbook, filename);
};

const LowStockReportCard = () => {
  const [stockFilter, setStockFilter] = useState('low_stock');
  const [reportData, setReportData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = async (params = { stockFilter }) => {
    try {
      setLoading(true);
      setError('');

      const res = await fetchLowStockReport(params);
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to fetch low stock report:', err);
      setError(
        err?.response?.data?.message || 'Failed to load low stock report.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport({ stockFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (value) => {
    setStockFilter(value);
    loadReport({ stockFilter: value });
  };

  const handleDownload = () => {
    downloadLowStockExcel({
      filename: `low-stock-report-${stockFilter}.xlsx`,
      reportData,
    });
  };

  const summary = reportData?.summary || {};
  const rows = buildLowStockRows(reportData);
  const totalLowStockItems =
  Number(summary.lowStockItems || 0) +
  Number(summary.criticallyLowStockItems || 0) +
  Number(summary.outOfStockItems || 0);

  return (
    <>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '22px',
          p: { xs: 2, sm: 3 },
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
          background: '#fff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '16px',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <RemoveCircleOutline
              sx={{
                color: '#dc2626',
                fontSize: 30,
              }}
            />
          </Box>

          <Chip
            label={`${formatNumber(totalLowStockItems)} ${
    totalLowStockItems === 1 ? 'item' : 'items'
  }`}
            sx={{
                borderRadius: '999px',
                background: '#fee2e2',
                color: '#991b1b',
                fontWeight: 800,
                fontSize: 14,
                height: 34,
                px: 1,
            }}
            />
        </Box>

        <Typography
          sx={{
            fontSize: 23,
            fontWeight: 900,
            color: '#111827',
            mb: 1,
          }}
        >
          Low Stock Report
        </Typography>

        <Typography
          sx={{
            fontSize: 15.5,
            lineHeight: 1.65,
            color: '#6b7280',
            mb: 2.5,
          }}
        >
          Low stock, critically low stock and out-of-stock items that need restocking attention.
        </Typography>

        {/* Stock filter controls */}
        <Box
          sx={{
            mb: 2.5,
            borderRadius: '20px',
            backgroundColor: '#f3f4f6',
            p: 1.4,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(3, 1fr)',
              },
              gap: 0.8,
            }}
          >
            {STOCK_FILTERS.map((item) => {
              const active = stockFilter === item.key;

              return (
                <Button
                  key={item.key}
                  onClick={() => handleFilterChange(item.key)}
                  disableRipple
                  sx={{
                    height: 40,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 900,
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                    letterSpacing: '0.01em',
                    backgroundColor: active ? '#ffffff' : 'transparent',
                    color: active ? '#111827' : '#6b7280',
                    boxShadow: active
                      ? 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 4px 12px rgba(15, 23, 42, 0.10)'
                      : 'none',
                    '&:hover': {
                      backgroundColor: active
                        ? '#ffffff'
                        : 'rgba(255,255,255,0.45)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Main metrics */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1.2,
            mb: 2.5,
          }}
        >
          <MiniMetric label="Low Stock" value={formatNumber(summary.lowStockItems)} />
          <MiniMetric
            label="Critical"
            value={formatNumber(summary.criticallyLowStockItems)}
          />
          <MiniMetric
            label="Out of Stock"
            value={formatNumber(summary.outOfStockItems)}
          />
        </Box>

        {/* Buttons */}
        <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '150px 1fr',
            },
            gap: 1.4,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              borderColor: '#e5e7eb',
              color: '#374151',
              fontWeight: 800,
              fontSize: 15,
              textTransform: 'none',
              background: '#fff',
              '&:hover': {
                borderColor: '#cbd5e1',
                background: '#f9fafb',
              },
            }}
          >
            Preview
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={handleDownload}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              background: '#0f172a',
              color: '#fff',
              fontWeight: 900,
              fontSize: 15,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                background: '#111827',
                boxShadow: 'none',
              },
              '&:disabled': {
                background: '#94a3b8',
                color: '#fff',
              },
            }}
          >
            Download Excel
          </Button>
        </Box>
        </Box>
      </Card>

      <LowStockReportPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        stockFilter={stockFilter}
        summary={summary}
        rows={rows}
      />
    </>
  );
};

const MiniMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        borderRadius: '14px',
        background: '#f8fafc',
        border: '1px solid #eef2f7',
        p: 1.4,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: '#6b7280',
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 15,
          color: '#111827',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const LowStockReportPreviewModal = ({
  open,
  onClose,
  stockFilter,
  summary,
  rows,
}) => {
  const visibleRows = rows || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Low Stock Report Preview
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#6b7280',
              mt: 0.4,
            }}
          >
            Filter: {getFilterLabel(stockFilter)}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: '#f8fafc',
        }}
      >
        <Box sx={{ p: 2.5, backgroundColor: '#ffffff' }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
              mb: 1.5,
            }}
          >
            Summary
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 1.4,
            }}
          >
            <PreviewMetric label="Low Stock" value={formatNumber(summary.lowStockItems)} />
            <PreviewMetric
              label="Critically Low"
              value={formatNumber(summary.criticallyLowStockItems)}
            />
            <PreviewMetric
              label="Out of Stock"
              value={formatNumber(summary.outOfStockItems)}
            />
            <PreviewMetric
              label="Selected Items"
              value={formatNumber(summary.totalRows)}
            />
            <PreviewMetric
              label="Low Stock Value"
              value={formatMoney(summary.lowStockValue)}
            />
            <PreviewMetric
              label="Critical Stock Value"
              value={formatMoney(summary.criticallyLowStockValue)}
            />
            <PreviewMetric
              label="Out of Stock Value"
              value={formatMoney(summary.outOfStockValue)}
            />
            <PreviewMetric
              label="Total Value"
              value={formatMoney(summary.totalStockValue)}
            />
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Low Stock Details
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#6b7280',
              fontWeight: 600,
              mt: 0.4,
            }}
          >
            These are the same rows included in the downloaded Excel sheet.
          </Typography>
        </Box>

        {visibleRows.length === 0 ? (
          <Box sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography color="text.secondary">
              No stock found for this filter.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: 2,
              backgroundColor: '#ffffff',
            }}
          >
            <Box
              sx={{
                maxHeight: '55vh',
                overflow: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '14px',
                backgroundColor: '#ffffff',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1250,
                  '& th': {
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #d1d5db',
                  },
                  '& td': {
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #eef2f7',
                    fontSize: 13,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Strength</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Current Stock</TableCell>
                    <TableCell align="right">Low Stock Level</TableCell>
                    <TableCell align="right">Critical Stock Level</TableCell>
                    <TableCell>Stock Status</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    <TableCell>Expiry Status</TableCell>
                    <TableCell align="right">Wholesale Price</TableCell>
                    <TableCell align="right">Stock Value</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.map((row, index) => (
                    <TableRow key={`${row.Item}-${row.Batch}-${index}`}>
                      <TableCell>{row.Item}</TableCell>
                      <TableCell>{row.Batch}</TableCell>
                      <TableCell>{row.Strength}</TableCell>
                      <TableCell>{row.Supplier}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row['Current Stock'])}
                      </TableCell>
                      <TableCell align="right">
                        {row['Low Stock Level']}
                      </TableCell>
                      <TableCell align="right">
                        {row['Critical Stock Level']}
                      </TableCell>
                      <TableCell>{row['Stock Status']}</TableCell>
                      <TableCell>{row['Expiry Date']}</TableCell>
                      <TableCell>{row['Expiry Status']}</TableCell>
                      <TableCell align="right">
                        {formatMoney(row['Wholesale Price'])}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(row['Stock Value'])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PreviewMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          color: '#6b7280',
          fontSize: 12,
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#111827',
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default LowStockReportCard;